import { Controller } from "stimulus"
import t from "../i18n"

export default class extends Controller {
  static targets = ["palette", "workspace", "canvasShell", "canvas", "connections", "inspector", "graphInput", "zoomLabel"]

  connect() {
    this.modules = this.parseJson(this.element.dataset.automationEditorModules, {})
    this.graph = this.parseJson(this.element.dataset.automationEditorGraph, { nodes: [], edges: [] })
    this.readonly = this.element.dataset.automationEditorReadonly === "true"
    this.modulesByKey = this.flattenModules(this.modules)
    this.zoom = 1
    this.selectedNodeId = null
    this.selectedEdgeId = null
    this.connectionDrag = null
    this.dragState = null
    this.panState = null
    this.spacePressed = false
    this.boundDragMove = this.dragMove.bind(this)
    this.boundDragEnd = this.dragEnd.bind(this)
    this.boundConnectionMove = this.connectionMove.bind(this)
    this.boundConnectionEnd = this.connectionEnd.bind(this)
    this.boundPanStart = this.panStart.bind(this)
    this.boundPanMove = this.panMove.bind(this)
    this.boundPanEnd = this.panEnd.bind(this)
    this.boundKeyDown = this.keyDown.bind(this)
    this.boundKeyUp = this.keyUp.bind(this)
    this.boundFullscreenChange = this.fullscreenChange.bind(this)

    this.canvasTarget.addEventListener("mousedown", this.boundPanStart)
    document.addEventListener("keydown", this.boundKeyDown)
    document.addEventListener("keyup", this.boundKeyUp)
    document.addEventListener("fullscreenchange", this.boundFullscreenChange)

    this.renderPalette()
    this.render()
  }

  disconnect() {
    if (this.hasCanvasTarget) this.canvasTarget.removeEventListener("mousedown", this.boundPanStart)
    document.removeEventListener("mousemove", this.boundDragMove)
    document.removeEventListener("mouseup", this.boundDragEnd)
    document.removeEventListener("mousemove", this.boundConnectionMove)
    document.removeEventListener("mouseup", this.boundConnectionEnd)
    document.removeEventListener("mousemove", this.boundPanMove)
    document.removeEventListener("mouseup", this.boundPanEnd)
    document.removeEventListener("keydown", this.boundKeyDown)
    document.removeEventListener("keyup", this.boundKeyUp)
    document.removeEventListener("fullscreenchange", this.boundFullscreenChange)
  }

  beforeSubmit() {
    this.syncGraphInput()
  }

  addNode(event) {
    event.preventDefault()
    if (this.readonly) return

    const key = event.currentTarget.dataset.moduleKey
    const definition = this.modulesByKey[key]
    if (!definition) return

    const count = this.graph.nodes.length
    const node = {
      id: `${key.replace(/\W/g, "-")}-${Date.now()}`,
      module_key: key,
      label: definition.label,
      config: this.defaultConfig(definition),
      position: { x: 180 + (count % 4) * 230, y: 120 + Math.floor(count / 4) * 150 }
    }

    this.graph.nodes.push(node)
    this.selectedNodeId = node.id
    this.render()
  }

  selectNode(event) {
    const nodeId = event.currentTarget.dataset.nodeId
    this.selectedNodeId = nodeId
    this.selectedEdgeId = null
    this.render()
  }

  startConnection(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.readonly || event.button !== 0) return

    const start = this.elementCenterInCanvas(event.currentTarget)
    this.connectionDrag = {
      mode: "create",
      source: event.currentTarget.dataset.nodeId,
      handle: event.currentTarget.dataset.handle,
      start: start,
      current: start,
      snapTarget: null,
      moved: false
    }
    this.selectedNodeId = this.connectionDrag.source
    this.selectedEdgeId = null
    this.canvasTarget.classList.add("is-linking")
    document.addEventListener("mousemove", this.boundConnectionMove)
    document.addEventListener("mouseup", this.boundConnectionEnd)
    this.renderInspector()
    this.renderConnections()
  }

  startReconnect(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.readonly || event.button !== 0) return

    const edgeId = event.currentTarget.dataset.edgeId
    const edge = this.graph.edges.find(candidate => candidate.id === edgeId)
    if (!edge) return

    const start = this.sourcePointForEdge(edge)
    this.connectionDrag = {
      mode: "reconnect",
      edgeId: edge.id,
      source: edge.source,
      handle: edge.source_handle || "success",
      start: start,
      current: this.clientPointToCanvas(event),
      snapTarget: null,
      moved: false
    }
    this.selectedNodeId = edge.source
    this.selectedEdgeId = edge.id
    this.canvasTarget.classList.add("is-linking")
    document.addEventListener("mousemove", this.boundConnectionMove)
    document.addEventListener("mouseup", this.boundConnectionEnd)
    this.renderInspector()
    this.renderConnections()
  }

  startConnectionClick(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.readonly) return

    const source = event.currentTarget.dataset.nodeId
    const handle = event.currentTarget.dataset.handle
    const start = this.elementCenterInCanvas(event.currentTarget)

    if (this.connectionDrag && this.connectionDrag.mode === "click" && this.connectionDrag.source === source && this.connectionDrag.handle === handle) {
      this.clearConnectionDrag()
      this.render()
      return
    }

    this.connectionDrag = {
      mode: "click",
      source: source,
      handle: handle,
      start: start,
      current: start,
      snapTarget: null,
      moved: false
    }
    this.selectedNodeId = source
    this.selectedEdgeId = null
    this.canvasTarget.classList.add("is-linking")
    this.render()
  }

  connectToInput(event) {
    event.preventDefault()
    event.stopPropagation()
    const target = event.currentTarget.dataset.nodeId

    if (this.connectionDrag && this.connectionDrag.mode === "click") {
      if (!this.readonly && target !== this.connectionDrag.source) {
        this.createOrUpdateEdge(this.connectionDrag.source, target, this.connectionDrag.handle, this.connectionDrag.edgeId)
      }
      this.clearConnectionDrag()
      this.render()
      return
    }

    const incomingEdge = this.graph.edges.find(edge => edge.target === target)
    if (incomingEdge) {
      this.selectedNodeId = null
      this.selectedEdgeId = incomingEdge.id
      this.render()
      return
    }

    this.selectedNodeId = target
    this.selectedEdgeId = null
    this.render()
  }

  selectEdge(event) {
    event.preventDefault()
    event.stopPropagation()

    this.selectedNodeId = null
    this.selectedEdgeId = event.currentTarget.dataset.edgeId
    this.render()
  }

  connectionMove(event) {
    if (!this.connectionDrag) return

    const current = this.clientPointToCanvas(event)
    const snapTarget = this.nearestInputHandle(current)
    if (Math.hypot(current.x - this.connectionDrag.current.x, current.y - this.connectionDrag.current.y) > 3) {
      this.connectionDrag.moved = true
    }
    this.connectionDrag.current = snapTarget ? snapTarget.point : current
    this.connectionDrag.snapTarget = snapTarget
    this.updateSnapTargets()
    this.renderConnections()
  }

  connectionEnd(event) {
    if (!this.connectionDrag) return

    const drag = this.connectionDrag
    const snapTarget = this.nearestInputHandle(this.clientPointToCanvas(event)) || this.connectionDrag.snapTarget
    if (snapTarget && snapTarget.nodeId !== this.connectionDrag.source) {
      this.createOrUpdateEdge(
        this.connectionDrag.source,
        snapTarget.nodeId,
        this.connectionDrag.handle,
        this.connectionDrag.edgeId
      )
    } else if (drag.mode === "create" && !drag.moved) {
      document.removeEventListener("mousemove", this.boundConnectionMove)
      document.removeEventListener("mouseup", this.boundConnectionEnd)
      this.connectionDrag = {
        ...drag,
        mode: "click",
        current: drag.start,
        snapTarget: null
      }
      this.canvasTarget.classList.add("is-linking")
      this.render()
      return
    } else if (drag.mode === "reconnect" && !drag.moved) {
      this.selectedNodeId = null
      this.selectedEdgeId = drag.edgeId
    }

    this.clearConnectionDrag()
    this.render()
  }

  clearConnectionDrag() {
    document.removeEventListener("mousemove", this.boundConnectionMove)
    document.removeEventListener("mouseup", this.boundConnectionEnd)
    this.connectionDrag = null
    if (this.hasCanvasTarget) {
      this.canvasTarget.classList.remove("is-linking")
      this.updateSnapTargets()
    }
  }

  clearSelection(event) {
    if (event) event.preventDefault()
    this.selectedNodeId = null
    this.selectedEdgeId = null
    if (this.connectionDrag) this.clearConnectionDrag()
    this.render()
  }

  deleteSelected(event) {
    event.preventDefault()
    if (this.readonly || !this.selectedNodeId) return

    const nodeId = this.selectedNodeId
    this.graph.nodes = this.graph.nodes.filter(node => node.id !== nodeId)
    this.graph.edges = this.graph.edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId)
    this.selectedNodeId = null
    this.selectedEdgeId = null
    this.render()
  }

  deleteEdge(event) {
    event.preventDefault()
    if (this.readonly) return

    const edgeId = event.currentTarget.dataset.edgeId
    this.graph.edges = this.graph.edges.filter(edge => edge.id !== edgeId)
    if (this.selectedEdgeId === edgeId) this.selectedEdgeId = null
    this.render()
  }

  deleteSelectedEdge(event) {
    event.preventDefault()
    if (this.readonly || !this.selectedEdgeId) return

    this.graph.edges = this.graph.edges.filter(edge => edge.id !== this.selectedEdgeId)
    this.selectedEdgeId = null
    this.render()
  }

  updateSelectedEdgeSource(event) {
    const edge = this.selectedEdge()
    if (!edge) return

    edge.source = event.currentTarget.value
    const handles = this.outputHandlesForNode(edge.source)
    if (!handles.includes(edge.source_handle || "success")) edge.source_handle = handles[0] || "success"
    if (edge.source === edge.target) {
      const fallbackTarget = this.graph.nodes.find(node => node.id !== edge.source)
      edge.target = fallbackTarget ? fallbackTarget.id : ""
    }
    this.render()
  }

  updateSelectedEdgeHandle(event) {
    const edge = this.selectedEdge()
    if (!edge) return

    edge.source_handle = event.currentTarget.value
    this.render()
  }

  updateSelectedEdgeTarget(event) {
    const edge = this.selectedEdge()
    if (!edge) return

    edge.target = event.currentTarget.value
    this.render()
  }

  updateLabel(event) {
    const node = this.selectedNode()
    if (!node) return

    node.label = event.currentTarget.value
    this.renderCanvas()
    this.syncGraphInput()
  }

  updateConfig(event) {
    const node = this.selectedNode()
    if (!node) return

    const key = event.currentTarget.dataset.configKey
    const type = event.currentTarget.type
    node.config = node.config || {}
    node.config[key] = type === "checkbox" ? event.currentTarget.checked : event.currentTarget.value
    this.renderCanvas()
    this.syncGraphInput()
  }

  autoLayout(event) {
    event.preventDefault()
    if (this.readonly) return

    this.graph.nodes.forEach((node, index) => {
      node.position = {
        x: 140 + (index % 4) * 250,
        y: 120 + Math.floor(index / 4) * 170
      }
    })
    this.render()
  }

  zoomIn(event) {
    event.preventDefault()
    this.setZoom(this.zoom + 0.1)
  }

  zoomOut(event) {
    event.preventDefault()
    this.setZoom(this.zoom - 0.1)
  }

  resetZoom(event) {
    event.preventDefault()
    this.setZoom(1)
  }

  toggleFullscreen(event) {
    event.preventDefault()
    const target = this.hasWorkspaceTarget ? this.workspaceTarget : this.element

    if (document.fullscreenElement) {
      document.exitFullscreen()
      return
    }

    if (target.requestFullscreen) target.requestFullscreen()
  }

  setZoom(value) {
    this.zoom = Math.max(0.4, Math.min(2, Math.round(value * 10) / 10))
    this.renderCanvas()
    this.updateZoomLabel()
  }

  startDrag(event, nodeId) {
    if (this.readonly) return
    if (event.target.closest("button")) return
    if (this.spacePressed || event.button === 1) return

    const node = this.graph.nodes.find(candidate => candidate.id === nodeId)
    if (!node) return

    const canvasRect = this.canvasTarget.getBoundingClientRect()
    this.dragState = {
      node: node,
      startX: event.clientX,
      startY: event.clientY,
      originalX: Number(node.position.x || 0),
      originalY: Number(node.position.y || 0),
      canvasLeft: canvasRect.left,
      canvasTop: canvasRect.top
    }
    this.selectedNodeId = nodeId
    document.addEventListener("mousemove", this.boundDragMove)
    document.addEventListener("mouseup", this.boundDragEnd)
  }

  dragMove(event) {
    if (!this.dragState) return

    const dx = (event.clientX - this.dragState.startX) / this.zoom
    const dy = (event.clientY - this.dragState.startY) / this.zoom
    this.dragState.node.position = {
      x: Math.max(20, this.dragState.originalX + dx),
      y: Math.max(20, this.dragState.originalY + dy)
    }
    this.renderCanvas()
    this.renderInspector()
    this.syncGraphInput()
  }

  dragEnd() {
    document.removeEventListener("mousemove", this.boundDragMove)
    document.removeEventListener("mouseup", this.boundDragEnd)
    this.dragState = null
  }

  panStart(event) {
    const isBackground = event.target === this.canvasTarget || event.target === this.connectionsTarget
    if (!(isBackground || this.spacePressed || event.button === 1)) return

    event.preventDefault()
    this.panState = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: this.canvasTarget.scrollLeft,
      scrollTop: this.canvasTarget.scrollTop
    }
    this.canvasTarget.classList.add("is-panning")
    document.addEventListener("mousemove", this.boundPanMove)
    document.addEventListener("mouseup", this.boundPanEnd)
  }

  panMove(event) {
    if (!this.panState) return

    this.canvasTarget.scrollLeft = this.panState.scrollLeft - (event.clientX - this.panState.startX)
    this.canvasTarget.scrollTop = this.panState.scrollTop - (event.clientY - this.panState.startY)
  }

  panEnd() {
    document.removeEventListener("mousemove", this.boundPanMove)
    document.removeEventListener("mouseup", this.boundPanEnd)
    this.canvasTarget.classList.remove("is-panning")
    this.panState = null
  }

  keyDown(event) {
    if (event.key === "Escape" && this.connectionDrag) {
      this.clearConnectionDrag()
      this.render()
      return
    }

    if (event.code !== "Space") return
    if (this.isTyping(event.target)) return

    this.spacePressed = true
    if (this.hasCanvasTarget) this.canvasTarget.classList.add("is-pan-ready")
  }

  keyUp(event) {
    if (event.code !== "Space") return

    this.spacePressed = false
    if (this.hasCanvasTarget) this.canvasTarget.classList.remove("is-pan-ready")
  }

  fullscreenChange() {
    const target = this.hasWorkspaceTarget ? this.workspaceTarget : this.element
    target.classList.toggle("is-fullscreen", document.fullscreenElement === target)
    this.renderCanvas()
  }

  renderPalette() {
    if (!this.hasPaletteTarget) return

    this.paletteTarget.innerHTML = ""
    Object.keys(this.modules).forEach(category => {
      const group = document.createElement("div")
      group.className = "automation-palette__group"

      const title = document.createElement("h3")
      title.textContent = this.categoryLabel(category)
      group.appendChild(title)

      this.modules[category].forEach(definition => {
        const button = document.createElement("button")
        button.type = "button"
        button.className = `automation-module automation-module--${definition.category}`
        button.dataset.moduleKey = definition.key
        button.addEventListener("click", this.addNode.bind(this))

        const label = document.createElement("strong")
        label.textContent = definition.label
        const description = document.createElement("span")
        description.textContent = definition.description

        button.appendChild(label)
        button.appendChild(description)
        group.appendChild(button)
      })

      this.paletteTarget.appendChild(group)
    })
  }

  render() {
    this.renderCanvas()
    this.renderInspector()
    this.syncGraphInput()
  }

  renderCanvas() {
    const existingNodes = this.canvasTarget.querySelectorAll(".automation-node")
    existingNodes.forEach(node => node.remove())
    this.canvasTarget.querySelectorAll(".automation-canvas-alert").forEach(alert => alert.remove())

    const validation = this.validateGraph()
    this.renderCanvasAlerts(validation)

    this.graph.nodes.forEach(node => {
      const definition = this.modulesByKey[node.module_key] || {}
      const nodeErrors = validation.nodeErrors[node.id] || []
      const element = document.createElement("div")
      element.className = `automation-node automation-node--${definition.category || "unknown"}`
      if (node.id === this.selectedNodeId) element.classList.add("is-selected")
      if (nodeErrors.length) element.classList.add("has-errors")
      if (this.connectionDrag && this.connectionDrag.source === node.id) element.classList.add("is-connecting")
      element.dataset.nodeId = node.id
      element.style.left = `${(node.position && node.position.x ? node.position.x : 80) * this.zoom}px`
      element.style.top = `${(node.position && node.position.y ? node.position.y : 80) * this.zoom}px`
      element.style.transform = `scale(${this.zoom})`
      element.style.transformOrigin = "top left"
      element.addEventListener("click", this.selectNode.bind(this))
      element.addEventListener("mousedown", event => this.startDrag(event, node.id))

      const header = document.createElement("div")
      header.className = "automation-node__header"
      const icon = document.createElement("i")
      icon.className = this.iconFor(definition.category)
      const title = document.createElement("strong")
      title.textContent = node.label || definition.label || node.module_key
      header.appendChild(icon)
      header.appendChild(title)

      const key = document.createElement("small")
      key.textContent = node.module_key

      if (nodeErrors.length) {
        const warning = document.createElement("button")
        warning.type = "button"
        warning.className = "automation-node__warning"
        warning.title = nodeErrors.join("\n")
        warning.innerHTML = `<i class="fa-solid fa-triangle-exclamation"></i><span>${nodeErrors.length}</span>`
        warning.addEventListener("click", event => {
          event.preventDefault()
          event.stopPropagation()
          this.selectedNodeId = node.id
          this.selectedEdgeId = null
          this.render()
        })
        element.appendChild(warning)
      }

      const inputHandle = document.createElement("button")
      inputHandle.type = "button"
      inputHandle.className = "automation-node__input-handle"
      inputHandle.dataset.nodeId = node.id
      inputHandle.title = t("automation_editor.input_handle_title", {}, "Input: anklicken oder Verbindung hier loslassen")
      inputHandle.innerHTML = '<i class="fa-solid fa-bullseye"></i>'
      if (this.connectionDrag && this.connectionDrag.snapTarget && this.connectionDrag.snapTarget.nodeId === node.id) {
        inputHandle.classList.add("is-snap-target")
      }
      inputHandle.addEventListener("click", this.connectToInput.bind(this))

      const outputs = document.createElement("div")
      outputs.className = "automation-node__outputs"
      ;(definition.outputs || ["success"]).forEach(handle => {
        const button = document.createElement("button")
        button.type = "button"
        button.className = `automation-node__output-handle automation-node__output-handle--${this.connectorKindForHandle(handle)}`
        button.dataset.nodeId = node.id
        button.dataset.handle = handle
        button.title = `Output ${this.connectionHandleLabel(handle)}: anklicken oder ziehen`
        button.innerHTML = `<i class="${this.connectionHandleIcon(handle)}"></i><span>${this.escape(this.connectionHandleLabel(handle))}</span>`
        button.addEventListener("mousedown", this.startConnection.bind(this))
        button.addEventListener("click", event => {
          event.preventDefault()
          event.stopPropagation()
        })
        outputs.appendChild(button)
      })

      element.appendChild(inputHandle)
      element.appendChild(header)
      element.appendChild(key)
      if (!this.readonly) element.appendChild(outputs)
      this.canvasTarget.appendChild(element)
    })

    this.resizeCanvasForZoom()
    this.renderConnections()
    this.updateZoomLabel()
  }

  renderCanvasAlerts(validation) {
    if (!validation.globalErrors.length) return

    const alert = document.createElement("div")
    alert.className = "automation-canvas-alert"
    alert.innerHTML = `
      <i class="fa-solid fa-triangle-exclamation"></i>
      <div>
        <strong>${this.escape(t("automation_editor.workflow_incomplete", {}, "Workflow noch nicht vollständig"))}</strong>
        <span>${validation.globalErrors.map(error => this.escape(error)).join(" ")}</span>
      </div>
    `
    this.canvasTarget.appendChild(alert)
  }

  renderConnections() {
    this.connectionsTarget.innerHTML = ""

    this.graph.edges.forEach(edge => {
      if (this.connectionDrag && this.connectionDrag.mode === "reconnect" && this.connectionDrag.edgeId === edge.id) return

      const start = this.sourcePointForEdge(edge)
      const end = this.targetPointForEdge(edge)
      if (!start || !end) return

      this.drawConnection(start, end, edge.source_handle || "success", edge.id)
    })

    if (this.connectionDrag) {
      this.drawConnection(
        this.connectionDrag.start,
        this.connectionDrag.current,
        this.connectionDrag.handle,
        null,
        true
      )
    }
  }

  drawConnection(start, end, handle, edgeId = null, draft = false) {
    const mid = Math.max(70 * this.zoom, Math.abs(end.x - start.x) / 2)
    const group = document.createElementNS("http://www.w3.org/2000/svg", "g")
    const selectedClass = edgeId && edgeId === this.selectedEdgeId ? " is-selected" : ""
    group.setAttribute("class", `automation-connection-group automation-connection-group--${this.connectorKindForHandle(handle)}${selectedClass}`)

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("d", `M ${start.x} ${start.y} C ${start.x + mid} ${start.y}, ${end.x - mid} ${end.y}, ${end.x} ${end.y}`)
    path.setAttribute("class", `automation-connection${draft ? " automation-connection--draft" : ""}`)
    path.style.strokeWidth = `${2.5 * this.zoom}`
    group.appendChild(path)

    const label = document.createElementNS("http://www.w3.org/2000/svg", "text")
    label.setAttribute("x", String((start.x + end.x) / 2))
    label.setAttribute("y", String((start.y + end.y) / 2 - 10 * this.zoom))
    label.setAttribute("class", "automation-connection-label")
    label.style.fontSize = `${11 * this.zoom}px`
    label.textContent = this.connectionHandleLabel(handle)
    group.appendChild(label)

    const badge = document.createElementNS("http://www.w3.org/2000/svg", "circle")
    badge.setAttribute("cx", String(end.x))
    badge.setAttribute("cy", String(end.y))
    badge.setAttribute("r", String(12 * this.zoom))
    badge.setAttribute("class", `automation-connection-badge automation-connection-badge--${this.connectorKindForHandle(handle)}${draft ? " automation-connection-badge--draft" : ""}`)
    group.appendChild(badge)

    const icon = document.createElementNS("http://www.w3.org/2000/svg", "text")
    icon.setAttribute("x", String(end.x))
    icon.setAttribute("y", String(end.y + 4 * this.zoom))
    icon.setAttribute("class", "automation-connection-icon")
    icon.style.fontSize = `${12 * this.zoom}px`
    icon.textContent = this.svgIconForHandle(handle)
    group.appendChild(icon)

    if (edgeId && !this.readonly) {
      const reconnectHandle = document.createElementNS("http://www.w3.org/2000/svg", "circle")
      reconnectHandle.setAttribute("cx", String(end.x))
      reconnectHandle.setAttribute("cy", String(end.y))
      reconnectHandle.setAttribute("r", String(18 * this.zoom))
      reconnectHandle.setAttribute("class", "automation-connection-reconnect")
      reconnectHandle.dataset.edgeId = edgeId
      reconnectHandle.addEventListener("mousedown", this.startReconnect.bind(this))
      reconnectHandle.addEventListener("click", this.selectEdge.bind(this))
      group.appendChild(reconnectHandle)
    }

    this.connectionsTarget.appendChild(group)
  }

  sourcePointForEdge(edge) {
    const handle = this.canvasTarget.querySelector(`.automation-node__output-handle[data-node-id="${this.cssEscape(edge.source)}"][data-handle="${this.cssEscape(edge.source_handle || "success")}"]`)
    if (handle) return this.elementCenterInCanvas(handle)

    const source = this.canvasTarget.querySelector(`.automation-node[data-node-id="${this.cssEscape(edge.source)}"]`)
    return source ? this.sidePointInCanvas(source, "right") : null
  }

  targetPointForEdge(edge) {
    const handle = this.canvasTarget.querySelector(`.automation-node__input-handle[data-node-id="${this.cssEscape(edge.target)}"]`)
    if (handle) return this.elementCenterInCanvas(handle)

    const target = this.canvasTarget.querySelector(`.automation-node[data-node-id="${this.cssEscape(edge.target)}"]`)
    return target ? this.sidePointInCanvas(target, "left") : null
  }

  sidePointInCanvas(element, side) {
    const rect = element.getBoundingClientRect()
    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const x = side === "right" ? rect.right : rect.left
    return {
      x: x - canvasRect.left + this.canvasTarget.scrollLeft,
      y: rect.top + rect.height / 2 - canvasRect.top + this.canvasTarget.scrollTop
    }
  }

  elementCenterInCanvas(element) {
    const rect = element.getBoundingClientRect()
    const canvasRect = this.canvasTarget.getBoundingClientRect()
    return {
      x: rect.left + rect.width / 2 - canvasRect.left + this.canvasTarget.scrollLeft,
      y: rect.top + rect.height / 2 - canvasRect.top + this.canvasTarget.scrollTop
    }
  }

  clientPointToCanvas(event) {
    const canvasRect = this.canvasTarget.getBoundingClientRect()
    return {
      x: event.clientX - canvasRect.left + this.canvasTarget.scrollLeft,
      y: event.clientY - canvasRect.top + this.canvasTarget.scrollTop
    }
  }

  nearestInputHandle(point) {
    const threshold = Math.max(30, 38 * this.zoom)
    let nearest = null

    this.canvasTarget.querySelectorAll(".automation-node__input-handle").forEach(handle => {
      const nodeId = handle.dataset.nodeId
      if (this.connectionDrag && nodeId === this.connectionDrag.source) return

      const center = this.elementCenterInCanvas(handle)
      const distance = Math.hypot(center.x - point.x, center.y - point.y)
      if (distance <= threshold && (!nearest || distance < nearest.distance)) {
        nearest = { nodeId: nodeId, point: center, distance: distance }
      }
    })

    return nearest
  }

  updateSnapTargets() {
    const snapNodeId = this.connectionDrag && this.connectionDrag.snapTarget && this.connectionDrag.snapTarget.nodeId
    this.canvasTarget.querySelectorAll(".automation-node__input-handle").forEach(handle => {
      handle.classList.toggle("is-snap-target", handle.dataset.nodeId === snapNodeId)
    })
  }

  createOrUpdateEdge(source, target, handle, edgeId = null) {
    if (!source || !target || source === target) return

    if (edgeId) {
      const duplicate = this.graph.edges.some(edge => (
        edge.id !== edgeId &&
        edge.source === source &&
        edge.target === target &&
        (edge.source_handle || "success") === (handle || "success")
      ))
      if (duplicate) {
        this.graph.edges = this.graph.edges.filter(edge => edge.id !== edgeId)
        return
      }

      const edge = this.graph.edges.find(candidate => candidate.id === edgeId)
      if (edge) {
        edge.source = source
        edge.target = target
        edge.source_handle = handle || "success"
        this.selectedNodeId = null
        this.selectedEdgeId = edge.id
      }
      return
    }

    const duplicate = this.graph.edges.some(edge => (
      edge.source === source &&
      edge.target === target &&
      (edge.source_handle || "success") === (handle || "success")
    ))
    if (duplicate) return

    const edge = {
      id: `edge-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      source: source,
      target: target,
      source_handle: handle || "success"
    }
    this.graph.edges.push(edge)
    this.selectedNodeId = null
    this.selectedEdgeId = edge.id
  }

  resizeCanvasForZoom() {
    const maxX = this.graph.nodes.reduce((max, node) => Math.max(max, Number(node.position && node.position.x || 0)), 0)
    const maxY = this.graph.nodes.reduce((max, node) => Math.max(max, Number(node.position && node.position.y || 0)), 0)
    const width = Math.max(1800, (maxX + 520) * this.zoom)
    const height = Math.max(this.fullscreenActive() ? window.innerHeight - 96 : 760, (maxY + 360) * this.zoom)

    this.canvasTarget.style.minWidth = "0"
    this.canvasTarget.style.height = `${height}px`
    this.connectionsTarget.style.width = `${width}px`
    this.connectionsTarget.style.height = `${height}px`
    this.connectionsTarget.setAttribute("width", String(width))
    this.connectionsTarget.setAttribute("height", String(height))
  }

  updateZoomLabel() {
    if (this.hasZoomLabelTarget) this.zoomLabelTarget.textContent = `${Math.round(this.zoom * 100)}%`
  }

  fullscreenActive() {
    const target = this.hasWorkspaceTarget ? this.workspaceTarget : this.element
    return document.fullscreenElement === target
  }

  renderInspector() {
    if (!this.hasInspectorTarget) return
    const validation = this.validateGraph()

    const selectedEdge = this.selectedEdge()
    if (selectedEdge) {
      this.renderEdgeInspector(selectedEdge)
      return
    }

    const node = this.selectedNode()
    if (!node) {
      this.inspectorTarget.innerHTML = `
        <div class="automation-empty automation-empty--small">
          <i class="fa-solid fa-circle-nodes"></i>
          <strong>${this.escape(t("automation_editor.select_node", {}, "Node auswählen"))}</strong>
          <span>${this.escape(t("automation_editor.configuration_here", {}, "Konfiguration erscheint hier."))}</span>
          ${this.validationSummaryHtml(validation)}
        </div>
      `
      return
    }

    const definition = this.modulesByKey[node.module_key] || { config_schema: [] }
    const configFields = this.configFieldsHtml(definition, node, validation)
    const outgoingEdges = this.graph.edges.filter(edge => edge.source === node.id)
    const nodeErrors = validation.nodeErrors[node.id] || []

    this.inspectorTarget.innerHTML = `
      <div class="automation-inspector__section">
        <span class="automation-eyebrow">${definition.category || "Node"}</span>
        <h2>${definition.label || node.module_key}</h2>
        ${nodeErrors.length ? `<div class="automation-inspector-alert">${nodeErrors.map(error => `<span><i class="fa-solid fa-triangle-exclamation"></i> ${this.escape(error)}</span>`).join("")}</div>` : ""}
        <label>Label</label>
        <input class="form-control" data-action="input->automation-editor#updateLabel" value="${this.escape(node.label || "")}" ${this.readonly ? "disabled" : ""}>
      </div>
      <div class="automation-inspector__section">
        <h3>${this.escape(t("automation_editor.configuration", {}, "Konfiguration"))}</h3>
        ${configFields || `<p>${this.escape(t("automation_editor.no_configuration_needed", {}, "Dieses Modul braucht keine Konfiguration."))}</p>`}
      </div>
      <div class="automation-inspector__section">
        <h3>${this.escape(t("automation_editor.data_transfer", {}, "Datenübergabe"))}</h3>
        <label>Input Mapping</label>
        <textarea class="form-control automation-code-input" rows="4" data-config-key="input_mapping" data-action="input->automation-editor#updateConfig change->automation-editor#updateConfig" ${this.readonly ? "disabled" : ""}>${this.escape(node.config && node.config.input_mapping ? node.config.input_mapping : "")}</textarea>
        <small class="automation-help">${this.escape(t("automation_editor.input_mapping_help", {}, "JSON: {\"lead_id\":\"trigger_payload.lead_id\"} zieht Werte aus dem Run-Kontext in Modul-Felder."))}</small>
        <label>Output Mapping</label>
        <textarea class="form-control automation-code-input" rows="4" data-config-key="output_mapping" data-action="input->automation-editor#updateConfig change->automation-editor#updateConfig" ${this.readonly ? "disabled" : ""}>${this.escape(node.config && node.config.output_mapping ? node.config.output_mapping : "")}</textarea>
        <small class="automation-help">${this.escape(t("automation_editor.output_mapping_help", {}, "JSON: {\"task_id\":\"followup_task_id\"} speichert Outputs unter eigenen Kontextnamen."))}</small>
        ${this.outputHintHtml(definition)}
      </div>
      <div class="automation-inspector__section">
        <h3>${this.escape(t("automation_editor.outputs", {}, "Ausgänge"))}</h3>
        ${outgoingEdges.length ? outgoingEdges.map(edge => `
          <div class="automation-edge-row">
            <span>${this.escape(edge.source_handle || "success")} → ${this.escape(this.nodeLabel(edge.target))}</span>
            ${this.readonly ? "" : `<button type="button" data-edge-id="${edge.id}" data-action="automation-editor#deleteEdge"><i class="fa fa-times"></i></button>`}
          </div>
        `).join("") : `<p>${this.escape(t("automation_editor.no_connection", {}, "Noch keine Verbindung."))}</p>`}
      </div>
      ${this.readonly ? "" : `<button type="button" class="btn btn-sm btn-outline-danger" data-action="automation-editor#deleteSelected"><i class="fa fa-trash"></i> ${this.escape(t("automation_editor.delete_node", {}, "Node löschen"))}</button>`}
    `
  }

  renderEdgeInspector(edge) {
    const sourceNode = this.graph.nodes.find(node => node.id === edge.source)
    const handles = this.outputHandlesForNode(edge.source)
    const nodeOptions = this.graph.nodes.map(node => {
      const label = node.label || node.module_key || node.id
      return `<option value="${this.escape(node.id)}" ${node.id === edge.source ? "selected" : ""}>${this.escape(label)}</option>`
    }).join("")
    const targetOptions = this.graph.nodes.map(node => {
      const label = node.label || node.module_key || node.id
      return `<option value="${this.escape(node.id)}" ${node.id === edge.target ? "selected" : ""} ${node.id === edge.source ? "disabled" : ""}>${this.escape(label)}</option>`
    }).join("")
    const handleOptions = handles.map(handle => (
      `<option value="${this.escape(handle)}" ${String(edge.source_handle || "success") === String(handle) ? "selected" : ""}>${this.escape(this.connectionHandleLabel(handle))}</option>`
    )).join("")

    this.inspectorTarget.innerHTML = `
      <div class="automation-inspector__section">
        <span class="automation-eyebrow">${this.escape(t("automation_editor.connection", {}, "Verbindung"))}</span>
        <h2>${this.escape(this.nodeLabel(edge.source))} → ${this.escape(this.nodeLabel(edge.target))}</h2>
        <p class="automation-help">${this.escape(t("automation_editor.connection_help", {}, "Output- und Input-Connectoren koennen direkt hier bearbeitet werden."))}</p>
      </div>
      <div class="automation-inspector__section automation-connection-editor">
        <label>Output-Node</label>
        <select class="form-control" data-action="change->automation-editor#updateSelectedEdgeSource" ${this.readonly ? "disabled" : ""}>
          ${nodeOptions}
        </select>
        <label>Output-Connector</label>
        <select class="form-control" data-action="change->automation-editor#updateSelectedEdgeHandle" ${this.readonly ? "disabled" : ""}>
          ${handleOptions}
        </select>
        <label>Input-Node</label>
        <select class="form-control" data-action="change->automation-editor#updateSelectedEdgeTarget" ${this.readonly ? "disabled" : ""}>
          ${targetOptions}
        </select>
      </div>
      <div class="automation-inspector__section">
        <div class="automation-edge-summary">
          <span><i class="${this.connectionHandleIcon(edge.source_handle)}"></i> ${this.escape(sourceNode ? sourceNode.module_key : edge.source)}</span>
          <strong>${this.escape(this.connectionHandleLabel(edge.source_handle || "success"))}</strong>
          <span><i class="fa-solid fa-bullseye"></i> ${this.escape(edge.target || t("automation_editor.no_target", {}, "Kein Ziel"))}</span>
        </div>
      </div>
      ${this.readonly ? "" : `
        <button type="button" class="btn btn-sm btn-outline-danger" data-action="automation-editor#deleteSelectedEdge">
          <i class="fa fa-unlink"></i> ${this.escape(t("automation_editor.delete_connection", {}, "Verbindung lösen"))}
        </button>
      `}
    `
  }

  configFieldsHtml(definition, node, validation) {
    const schema = definition.config_schema || []
    if (!schema.length) return ""

    const config = node.config || {}
    const fieldErrors = (validation.fieldErrors[node.id] || {})
    const requiredFields = schema.filter(field => this.fieldRequired(field))
    const optionalFields = schema.filter(field => !this.fieldRequired(field))
    const requiredHtml = requiredFields.map(field => this.fieldHtml(field, config, fieldErrors)).join("")
    const optionalHtml = optionalFields.map(field => this.fieldHtml(field, config, fieldErrors)).join("")

    return `
      ${requiredFields.length ? `
        <div class="automation-config-group automation-config-group--required">
          <div class="automation-config-group__title">
            <strong>Pflichtfelder</strong>
            <span>${requiredFields.length}</span>
          </div>
          ${requiredHtml}
        </div>
      ` : ""}
      ${optionalFields.length ? `
        <div class="automation-config-group">
          <div class="automation-config-group__title">
            <strong>Optional</strong>
            <span>${optionalFields.length}</span>
          </div>
          ${optionalHtml}
        </div>
      ` : ""}
    `
  }

  fieldHtml(field, config, fieldErrors = {}) {
    const key = field.key
    const value = config[key] === undefined || config[key] === null ? "" : config[key]
    const disabled = this.readonly ? "disabled" : ""
    const required = field.required ? "required" : ""
    const action = `data-config-key="${this.escape(key)}" data-action="input->automation-editor#updateConfig change->automation-editor#updateConfig"`
    const label = `
      <label>
        ${this.escape(field.label)}
        ${this.fieldRequired(field) ? '<span class="automation-required-pill">Pflicht</span>' : ""}
      </label>
    `
    const error = fieldErrors[key] ? `<small class="automation-field-error"><i class="fa-solid fa-triangle-exclamation"></i> ${this.escape(fieldErrors[key])}</small>` : ""
    const wrapperClass = `automation-config-field${fieldErrors[key] ? " has-error" : ""}`
    let control = ""

    if (field.type === "textarea") {
      control = `<textarea class="form-control" ${action} ${required} ${disabled}>${this.escape(value)}</textarea>`
    } else if (field.type === "json") {
      control = `<textarea class="form-control automation-code-input" rows="7" ${action} ${required} ${disabled}>${this.escape(value)}</textarea>`
    } else if (field.type === "select") {
      const options = (field.options || []).map(option => `<option value="${this.escape(option)}" ${String(value) === String(option) ? "selected" : ""}>${this.escape(option)}</option>`).join("")
      control = `<select class="form-control" ${action} ${required} ${disabled}><option value=""></option>${options}</select>`
    } else if (field.type === "checkbox") {
      control = `<label class="automation-check"><input type="checkbox" ${action} ${value === true || value === "1" ? "checked" : ""} ${disabled}><span>${this.escape(field.label)}</span></label>`
      return `<div class="${wrapperClass}">${control}${error}</div>`
    } else {
      const inputType = field.type === "number" ? "number" : field.type === "datetime" ? "datetime-local" : field.type === "time" ? "time" : "text"
      control = `<input type="${inputType}" class="form-control" value="${this.escape(value)}" ${action} ${required} ${disabled}>`
    }

    return `<div class="${wrapperClass}">${label}${control}${error}</div>`
  }

  selectedNode() {
    return this.graph.nodes.find(node => node.id === this.selectedNodeId)
  }

  selectedEdge() {
    return this.graph.edges.find(edge => edge.id === this.selectedEdgeId)
  }

  outputHandlesForNode(nodeId) {
    const node = this.graph.nodes.find(candidate => candidate.id === nodeId)
    const definition = node ? this.modulesByKey[node.module_key] : null
    return (definition && definition.outputs) || ["success"]
  }

  validateGraph() {
    const result = { globalErrors: [], nodeErrors: {}, fieldErrors: {} }
    const nodes = this.graph.nodes || []
    const edges = this.graph.edges || []
    const nodeIds = nodes.map(node => node.id)
    const triggers = nodes.filter(node => this.triggerNode(node))

    if (!nodes.length) {
      result.globalErrors.push(t("automation_editor.validation.need_trigger_and_node", {}, "Lege mindestens einen Trigger und einen Folge-Node an."))
      return result
    }

    if (triggers.length === 0) {
      result.globalErrors.push(t("automation_editor.validation.trigger_missing", {}, "Es fehlt ein Trigger."))
    } else if (triggers.length > 1) {
      result.globalErrors.push(t("automation_editor.validation.single_trigger", {}, "Es darf genau ein Trigger vorhanden sein."))
      triggers.forEach(node => this.addValidationError(result, node.id, t("automation_editor.validation.multiple_triggers", {}, "Mehrere Trigger gefunden. Behalte nur einen Trigger.")))
    } else if (nodes.length > 1 && !edges.some(edge => edge.source === triggers[0].id)) {
      this.addValidationError(result, triggers[0].id, t("automation_editor.validation.trigger_unconnected", {}, "Trigger ist nicht mit einem nächsten Node verbunden."))
    }

    nodes.forEach(node => {
      const definition = this.modulesByKey[node.module_key]
      if (!definition) {
        this.addValidationError(result, node.id, t("automation_editor.validation.unknown_module", {}, "Unbekanntes Modul."))
        return
      }

      const config = node.config || {}
      const inputMapping = this.parseOptionalJson(config.input_mapping)
      if (config.input_mapping && !this.plainObject(inputMapping)) {
        this.addValidationError(result, node.id, t("automation_editor.validation.input_mapping_invalid", {}, "Input-Mapping ist kein gültiges JSON-Objekt."), "input_mapping")
      }
      if (config.output_mapping && !this.plainObject(this.parseOptionalJson(config.output_mapping))) {
        this.addValidationError(result, node.id, t("automation_editor.validation.output_mapping_invalid", {}, "Output-Mapping ist kein gültiges JSON-Objekt."), "output_mapping")
      }

      ;(definition.config_schema || []).forEach(field => {
        if (!this.fieldRequired(field)) return

        const key = field.key
        if (this.configValuePresent(config[key]) || (this.plainObject(inputMapping) && this.configValuePresent(inputMapping[key]))) return

        this.addValidationError(result, node.id, t("automation_editor.validation.field_missing", { field: field.label }, `${field.label} fehlt.`), key)
      })

      this.validateScheduleTrigger(result, node, config)
    })

    edges.forEach(edge => {
      if (!nodeIds.includes(edge.source)) result.globalErrors.push(t("automation_editor.validation.unknown_source", { source: edge.source }, `Verbindung mit unbekannter Quelle: ${edge.source}`))
      if (!nodeIds.includes(edge.target)) result.globalErrors.push(t("automation_editor.validation.unknown_target", { target: edge.target }, `Verbindung mit unbekanntem Ziel: ${edge.target}`))
    })

    result.globalErrors = [...new Set(result.globalErrors)]
    return result
  }

  validateScheduleTrigger(result, node, config) {
    if (node.module_key !== "trigger.schedule") return

    const scheduleType = config.schedule_type
    if (scheduleType === "once" && !this.configValuePresent(config.run_at)) {
      this.addValidationError(result, node.id, t("automation_editor.validation.once_needs_time", {}, "Einmaliger Zeitplan braucht einen Zeitpunkt."), "run_at")
    }
    if (["daily", "weekly", "monthly"].includes(scheduleType) && !this.configValuePresent(config.time_of_day)) {
      this.addValidationError(result, node.id, t("automation_editor.validation.schedule_needs_time", {}, "Zeitplan braucht eine Uhrzeit."), "time_of_day")
    }
    if (scheduleType === "weekly" && !this.configValuePresent(config.weekdays)) {
      this.addValidationError(result, node.id, t("automation_editor.validation.weekly_needs_weekdays", {}, "Wöchentlicher Zeitplan braucht Wochentage."), "weekdays")
    }
    if (scheduleType === "monthly" && !this.configValuePresent(config.day_of_month)) {
      this.addValidationError(result, node.id, t("automation_editor.validation.monthly_needs_day", {}, "Monatlicher Zeitplan braucht einen Tag im Monat."), "day_of_month")
    }
    if (scheduleType === "interval") {
      if (!this.configValuePresent(config.interval_value)) {
        this.addValidationError(result, node.id, t("automation_editor.validation.interval_needs_value", {}, "Intervall braucht einen Wert."), "interval_value")
      }
      if (!this.configValuePresent(config.interval_unit)) {
        this.addValidationError(result, node.id, t("automation_editor.validation.interval_needs_unit", {}, "Intervall braucht eine Einheit."), "interval_unit")
      }
    }
  }

  addValidationError(result, nodeId, message, fieldKey = null) {
    result.nodeErrors[nodeId] = result.nodeErrors[nodeId] || []
    if (!result.nodeErrors[nodeId].includes(message)) result.nodeErrors[nodeId].push(message)

    if (fieldKey) {
      result.fieldErrors[nodeId] = result.fieldErrors[nodeId] || {}
      result.fieldErrors[nodeId][fieldKey] = message
    }
  }

  validationSummaryHtml(validation) {
    if (!validation.globalErrors.length) return ""

    return `
      <div class="automation-inspector-alert">
        ${validation.globalErrors.map(error => `<span><i class="fa-solid fa-triangle-exclamation"></i> ${this.escape(error)}</span>`).join("")}
      </div>
    `
  }

  triggerNode(node) {
    const definition = this.modulesByKey[node.module_key]
    return (definition && definition.category === "trigger") || String(node.module_key || "").startsWith("trigger.")
  }

  fieldRequired(field) {
    return field.required === true || field.required === "true"
  }

  configValuePresent(value) {
    if (value === null || value === undefined) return false
    if (typeof value === "boolean") return value === true
    return String(value).trim().length > 0
  }

  parseOptionalJson(value) {
    if (!this.configValuePresent(value)) return {}
    return this.parseJson(value, null)
  }

  plainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value)
  }

  syncGraphInput() {
    if (this.hasGraphInputTarget) {
      this.graphInputTarget.value = JSON.stringify(this.graph)
    }
  }

  flattenModules(modules) {
    const byKey = {}
    Object.keys(modules || {}).forEach(category => {
      modules[category].forEach(definition => {
        byKey[definition.key] = definition
      })
    })
    return byKey
  }

  defaultConfig(definition) {
    const config = {}
    ;(definition.config_schema || []).forEach(field => {
      if (field.type === "checkbox") config[field.key] = false
      if (field.type === "json") config[field.key] = this.defaultJson(field.key, definition.key)
      if (field.key === "schedule_type") config[field.key] = "daily"
      if (field.key === "time_of_day") config[field.key] = "09:00"
      if (field.key === "task_type") config[field.key] = "Sonstiges"
      if (field.key === "operation") config[field.key] = "add"
      if (field.key === "operator") config[field.key] = "equals"
      if (field.key === "mode") config[field.key] = "all"
    })
    return config
  }

  defaultJson(key, moduleKey = null) {
    if (key === "rules_json") {
      if (moduleKey === "condition.switch") {
        return JSON.stringify([
          { handle: "case_1", path: "lead.status", operator: "equals", value: "new" },
          { handle: "case_2", path: "lead.status", operator: "equals", value: "qualified" }
        ], null, 2)
      }

      return JSON.stringify([
        { path: "lead.status", operator: "equals", value: "pending" }
      ], null, 2)
    }

    return ""
  }

  nodeLabel(nodeId) {
    const node = this.graph.nodes.find(candidate => candidate.id === nodeId)
    return node ? (node.label || node.module_key) : nodeId
  }

  parseJson(value, fallback) {
    try {
      return value ? JSON.parse(value) : fallback
    } catch (_error) {
      return fallback
    }
  }

  categoryLabel(category) {
    return {
      trigger: "Trigger",
      condition: t("automation_editor.categories.condition", {}, "Bedingungen"),
      action: t("automation_editor.categories.action", {}, "Aktionen"),
      agent: "AI-Jobs",
      extension: "Extensions"
    }[category] || category
  }

  connectionHandleLabel(handle) {
    const value = String(handle || "success")
    const labels = {
      success: t("automation_editor.handles.success", {}, "Weiter"),
      true: t("automation_editor.handles.true", {}, "Ja"),
      false: t("automation_editor.handles.false", {}, "Nein"),
      default: t("automation_editor.handles.default", {}, "Default")
    }
    if (labels[value]) return labels[value]
    if (value.match(/^case_\d+$/)) return value.replace("_", " ").replace(/\b\w/g, character => character.toUpperCase())
    return value.replace(/_/g, " ")
  }

  connectionHandleIcon(handle) {
    const kind = this.connectorKindForHandle(handle)
    return {
      success: "fa-solid fa-arrow-right",
      true: "fa-solid fa-check",
      false: "fa-solid fa-xmark",
      switch: "fa-solid fa-diamond-turn-right",
      default: "fa-solid fa-route"
    }[kind] || "fa-solid fa-circle-nodes"
  }

  connectorKindForHandle(handle) {
    const value = String(handle || "success")
    if (value === "true") return "true"
    if (value === "false") return "false"
    if (value === "default") return "default"
    if (value.match(/^case_/)) return "switch"
    return "success"
  }

  svgIconForHandle(handle) {
    const kind = this.connectorKindForHandle(handle)
    return {
      success: "→",
      true: "✓",
      false: "×",
      switch: "◇",
      default: "↳"
    }[kind] || "•"
  }

  iconFor(category) {
    return {
      trigger: "fa-solid fa-play",
      condition: "fa-solid fa-code-branch",
      action: "fa-solid fa-bolt",
      agent: "fa-solid fa-wand-magic-sparkles",
      extension: "fa-solid fa-plug-circle-bolt"
    }[category] || "fa-solid fa-circle"
  }

  outputHintHtml(definition) {
    const branchOutputs = definition.outputs || ["success"]
    const dataOutputs = definition.data_outputs || []
    const contextHints = ["trigger_payload.lead_id", "lead_id", "email_subject", "email_html_body", "task_id", "agent_summary", "last_condition_result"]

    return `
      <div class="automation-output-hints">
        <strong>${this.escape(t("automation_editor.output_hints.branch_outputs", {}, "Branch-Ausgänge"))}</strong>
        <span>${branchOutputs.map(output => this.escape(output)).join(", ")}</span>
        <strong>${this.escape(t("automation_editor.output_hints.data_outputs", {}, "Datenoutputs"))}</strong>
        <span>${dataOutputs.length ? dataOutputs.map(output => this.escape(output)).join(", ") : this.escape(t("automation_editor.output_hints.none_declared", {}, "keine deklariert"))}</span>
        <strong>${this.escape(t("automation_editor.output_hints.common_context_values", {}, "Häufige Kontextwerte"))}</strong>
        <span>${contextHints.map(output => this.escape(output)).join(", ")}</span>
      </div>
    `
  }

  escape(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  cssEscape(value) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(String(value || ""))

    return String(value || "").replace(/\\/g, "\\\\").replace(/"/g, '\\"')
  }

  isTyping(target) {
    return target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
  }
}
